const KATEX_JS = 'https://cdn.jsdelivr.net/npm/katex@0.16.22/dist/katex.min.js';

const FORMULA_ICON = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24"><path fill="currentColor" d="M4.2 4h15.6v2.15L13.4 12l6.4 5.85V20H4.2v-2.4l7.1-5.6-7.1-5.6V4zm2.3 2.05 6.35 5.05v.8L6.5 16.95h11.1v-.7L11.3 12l6.3-5.2V6.05H6.5z"/></svg>`;

let katexLoader = null;

const loadKatex = () => {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('KaTeX is only available in the browser'));
  }

  if (window.katex) {
    return Promise.resolve(window.katex);
  }

  if (katexLoader) {
    return katexLoader;
  }

  katexLoader = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = KATEX_JS;
    script.async = true;
    script.crossOrigin = 'anonymous';
    script.onload = () => {
      if (window.katex) {
        resolve(window.katex);
        return;
      }
      katexLoader = null;
      reject(new Error('KaTeX failed to load'));
    };
    script.onerror = () => {
      katexLoader = null;
      reject(new Error('KaTeX failed to load'));
    };
    document.head.appendChild(script);
  });

  return katexLoader;
};

const escapeHtmlAttr = (value) =>
  String(value)
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

const renderFormulaHtml = (katex, latex, displayMode) => {
  const rendered = katex.renderToString(latex, {
    throwOnError: false,
    displayMode,
    output: 'html',
  });

  return `<span class="jodit-formula" contenteditable="false" data-latex="${escapeHtmlAttr(
    latex,
  )}" data-display="${displayMode ? '1' : '0'}">${rendered}</span>`;
};

const openFormulaDialog = async (editorInstance, existingNode = null) => {
  let katex;
  try {
    katex = await loadKatex();
  } catch (err) {
    editorInstance.alert(
      'Could not load the formula editor. Please try again.',
    );
    return;
  }

  const dialog = editorInstance.dlg({
    closeOnClickOverlay: true,
    resizable: false,
    minWidth: 480,
  });

  const form = dialog.c.fromHTML(`<form class="jodit-formula-dialog">
    <label class="jodit-formula-dialog__label">LaTeX formula</label>
    <textarea class="jodit-input jodit-formula-dialog__input" rows="3" placeholder="Example: E = mc^2 or \\frac{a}{b}"></textarea>
    <label class="jodit-formula-dialog__check">
      <input type="checkbox" class="jodit-formula-dialog__display" />
      Display as block
    </label>
    <div class="jodit-formula-dialog__preview-label">Preview</div>
    <div class="jodit-formula-dialog__preview"></div>
  </form>`);

  const input = form.querySelector('.jodit-formula-dialog__input');
  const displayToggle = form.querySelector('.jodit-formula-dialog__display');
  const preview = form.querySelector('.jodit-formula-dialog__preview');

  const updatePreview = () => {
    const latex = input.value.trim();
    if (!latex) {
      preview.innerHTML =
        '<span class="jodit-formula-dialog__empty">Preview will appear here</span>';
      return;
    }

    try {
      preview.innerHTML = katex.renderToString(latex, {
        throwOnError: true,
        displayMode: displayToggle.checked,
      });
    } catch (err) {
      preview.innerHTML = `<span class="jodit-formula-dialog__error">${escapeHtmlAttr(
        err.message,
      )}</span>`;
    }
  };

  if (existingNode) {
    input.value = existingNode.getAttribute('data-latex') || '';
    displayToggle.checked = existingNode.getAttribute('data-display') === '1';
  }

  dialog.e.on(input, 'input', updatePreview);
  dialog.e.on(displayToggle, 'change', updatePreview);
  updatePreview();

  const insertFormula = () => {
    const latex = input.value.trim();
    if (!latex) {
      return false;
    }

    try {
      katex.renderToString(latex, {
        throwOnError: true,
        displayMode: displayToggle.checked,
      });
    } catch (err) {
      return false;
    }

    const html = renderFormulaHtml(katex, latex, displayToggle.checked);
    if (existingNode && existingNode.parentNode) {
      existingNode.outerHTML = html;
      editorInstance.synchronizeValues();
    } else {
      editorInstance.s.insertHTML(`${html}&nbsp;`);
    }

    dialog.close();
    return true;
  };

  dialog.e.on(form, 'submit', (event) => {
    event.preventDefault();
    insertFormula();
    return false;
  });

  const insertBtn = dialog.c.fromHTML(
    '<button type="button" class="jodit-ui-button jodit-ui-button_variant_primary">Insert</button>',
  );
  const cancelBtn = dialog.c.fromHTML(
    '<button type="button" class="jodit-ui-button">Cancel</button>',
  );

  dialog.e.on(insertBtn, 'click', insertFormula);
  dialog.e.on(cancelBtn, 'click', () => dialog.close());
  dialog.setFooter([insertBtn, cancelBtn]);
  dialog.open(
    form,
    existingNode ? 'Edit formula' : 'Insert formula',
    true,
    true,
  );
  input.focus();
};

export { openFormulaDialog };

export const getFormulaButton = () => ({
  name: 'formula',
  tooltip: 'Mathematical formula',
  icon: FORMULA_ICON,
  exec: (editorInstance) => {
    openFormulaDialog(editorInstance);
  },
});

export const bindFormulaEvents = (editorInstance) => {
  editorInstance.e.on('dblclick', (event) => {
    const formula = event.target?.closest?.('.jodit-formula');
    if (!formula) {
      return;
    }

    event.preventDefault();
    openFormulaDialog(editorInstance, formula);
  });
};
